/**
 * PublicSchoolSections — the bands of the premium public school page.
 * Motion is CSS-only: an IntersectionObserver adds `is-revealed`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users, GraduationCap, BookOpen, Award, CalendarDays, ArrowRight, Quote,
  BookMarked, FlaskConical, Trophy, Church, Home, Bus, Monitor, HeartPulse,
  Utensils, ShieldCheck, Mail, Phone, MapPin, Globe, Clock, X, Sparkles,
} from 'lucide-react';

export const FACILITIES = {
  library: { name: 'Library & reading rooms', icon: BookMarked },
  labs: { name: 'Science & innovation labs', icon: FlaskConical },
  sports: { name: 'Sports & athletics', icon: Trophy },
  chapel: { name: 'Chapel & worship centre', icon: Church },
  boarding: { name: 'Boarding & residences', icon: Home },
  transport: { name: 'School transport', icon: Bus },
  ict: { name: 'ICT & computer labs', icon: Monitor },
  clinic: { name: 'Health clinic', icon: HeartPulse },
  dining: { name: 'Dining hall', icon: Utensils },
  security: { name: '24/7 campus security', icon: ShieldCheck },
};

export const SCHOOL_TYPES = {
  kindergarten: 'Kindergarten & Early Years',
  primary: 'Primary School',
  secondary: 'Secondary School',
  vocational: 'Vocational & Technical',
  college: 'Tertiary College',
  university: 'University',
  bible_college: 'Seminary & Bible College',
  school: 'School',
  institute: 'Institute',
  campus: 'Campus',
};

export const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);
  return [ref, shown];
};

const Section = ({ id, children, className = '', testId }) => {
  const [ref, shown] = useReveal();
  return (
    <section
      id={id}
      ref={ref}
      data-testid={testId}
      className={`fw-reveal ${shown ? 'is-revealed' : ''} ${className}`}
    >
      {children}
    </section>
  );
};

export const SectionHead = ({ kicker, title, blurb, brand, light }) => (
  <div className="max-w-2xl">
    {kicker && (
      <p className="text-xs font-semibold tracking-[0.18em] uppercase mb-3"
         style={{ color: light ? 'rgba(255,255,255,0.65)' : brand }}>
        {kicker}
      </p>
    )}
    <h2 className={`fw-display text-3xl sm:text-4xl font-bold tracking-tight ${
      light ? 'text-white' : 'text-slate-900'}`}>
      {title}
    </h2>
    {blurb && (
      <p className={`mt-4 text-base leading-relaxed ${light ? 'text-white/70' : 'text-slate-600'}`}>
        {blurb}
      </p>
    )}
  </div>
);

const Counter = ({ value, run }) => {
  const numeric = String(value).replace(/[^0-9.]/g, '');
  const suffix = String(value).replace(/[0-9.,\s]/g, '');
  const target = parseFloat(numeric || '0');
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run || !target) return undefined;
    let frame;
    const started = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - started) / 1200);
      setN(target * (1 - (1 - p) ** 3));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [run, target]);
  const shown = target
    ? (Number.isInteger(target) ? Math.round(n).toLocaleString() : n.toFixed(1))
    : value;
  return <>{shown}{target ? suffix : ''}</>;
};

export const StatsStrip = ({ stats, brand }) => {
  const [ref, shown] = useReveal();
  const items = [
    { key: 'students', label: 'Students', icon: Users, value: stats.students },
    { key: 'teachers', label: 'Academic staff', icon: GraduationCap, value: stats.teachers },
    { key: 'programmes', label: 'Programmes', icon: BookOpen, value: stats.programmes },
    { key: 'graduation_rate', label: 'Graduation rate', icon: Award, value: stats.graduation_rate },
    { key: 'years', label: 'Years of service', icon: CalendarDays, value: stats.years },
  ].filter((i) => i.value !== '' && i.value !== null && i.value !== undefined && i.value !== '0');
  if (!items.length) return null;
  return (
    <div ref={ref} data-testid="school-stats-strip"
         className={`fw-reveal ${shown ? 'is-revealed' : ''} border-y border-slate-200 bg-white`}>
      <div className="mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-x divide-y lg:divide-y-0 divide-slate-100">
        {items.map((i) => (
          <div key={i.key} className="px-6 py-9 sm:px-8 sm:py-11" data-testid={`stat-${i.key}`}>
            <i.icon className="h-4 w-4 mb-4" style={{ color: brand }} />
            <div className="fw-display text-3xl sm:text-4xl font-bold text-slate-900 tabular-nums">
              <Counter value={i.value} run={shown} />
            </div>
            <div className="mt-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-400">
              {i.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AboutBand = ({ school, brand, typeLabel }) => {
  if (!school.about_us && !school.mission && !school.vision) return null;
  return (
    <Section id="about" testId="school-about-section" className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <SectionHead kicker={typeLabel} title={`About ${school.name}`} brand={brand} />
          {school.about_us && (
            <p className="mt-8 text-base leading-[1.9] text-slate-600 whitespace-pre-line">
              {school.about_us}
            </p>
          )}
        </div>
        <div className="lg:col-span-5 space-y-5">
          {[['Our mission', school.mission], ['Our vision', school.vision]]
            .filter(([, v]) => v).map(([label, v]) => (
              <div key={label} className="rounded-2xl bg-slate-50 p-7 border-l-2"
                   style={{ borderLeftColor: brand }}>
                <p className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-400">{label}</p>
                <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line">{v}</p>
              </div>
            ))}
        </div>
      </div>
    </Section>
  );
};

export const ProgrammesBand = ({ programmes, brand, applyUrl }) => (
  <Section id="programmes" testId="school-programmes-section" className="bg-slate-50/70 py-24">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHead kicker="What we teach" title="Academic programmes" brand={brand}
                   blurb="Every programme below is open for study. Pick the one that fits and apply online." />
      {programmes.length === 0 ? (
        <p className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white px-8 py-12 text-center text-sm text-slate-500"
           data-testid="programmes-empty">
          Programmes for the coming intake are being finalised. Speak to the admissions office to
          register your interest early.
        </p>
      ) : (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programmes.map((p, idx) => (
            <div key={p.id || `${p.name}-${idx}`}
                 data-testid={`programme-card-${idx}`}
                 style={{ animationDelay: `${Math.min(idx, 8) * 60}ms` }}
                 className="fw-stagger group rounded-2xl border border-slate-200 bg-white p-7 flex flex-col
                            transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]">
              <div className="flex items-start justify-between gap-3">
                <h3 className="fw-display text-lg font-semibold text-slate-900 leading-snug">{p.name}</h3>
                {p.code && <span className="text-[10px] font-mono text-slate-400 shrink-0 mt-1">{p.code}</span>}
              </div>
              {p.description && (
                <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-3">{p.description}</p>
              )}
              <div className="mt-5 flex flex-wrap gap-1.5">
                {p.award && (
                  <Badge variant="outline" className="text-[10px] capitalize border-0"
                         style={{ backgroundColor: `${brand}12`, color: brand }}>
                    {p.award}
                  </Badge>
                )}
                {(p.duration_years || p.duration_label) && (
                  <Badge variant="outline" className="text-[10px] bg-slate-50 border-slate-200 text-slate-600">
                    {p.duration_label || `${p.duration_years} ${p.duration_years === 1 ? 'year' : 'years'}`}
                  </Badge>
                )}
                {(p.study_modes || []).map((m) => (
                  <Badge key={m} variant="outline"
                         className="text-[10px] bg-slate-50 border-slate-200 text-slate-600 capitalize">
                    {String(m).replace('_', ' ')}
                  </Badge>
                ))}
              </div>
              <Link to={applyUrl} className="mt-6 pt-5 border-t border-slate-100 text-xs font-semibold
                                             flex items-center gap-1.5 group-hover:gap-2.5 transition-all"
                    style={{ color: brand }} data-testid={`programme-apply-${idx}`}>
                Apply for this programme <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  </Section>
);

export const FacilitiesBand = ({ facilities, note, brand }) => {
  if (!facilities.length) return null;
  return (
    <Section id="facilities" testId="school-facilities-section"
             className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <SectionHead kicker="On campus" title="Facilities" brand={brand} blurb={note} />
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {facilities.map((key, idx) => {
          const f = FACILITIES[key];
          if (!f) return null;
          return (
            <div key={key} data-testid={`facility-${key}`}
                 style={{ animationDelay: `${Math.min(idx, 8) * 60}ms` }}
                 className="fw-stagger flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-5
                            transition-colors hover:border-slate-300">
              <span className="rounded-xl p-2.5 shrink-0" style={{ backgroundColor: `${brand}12` }}>
                <f.icon className="h-5 w-5" style={{ color: brand }} />
              </span>
              <span className="text-sm font-medium text-slate-800 leading-snug">{f.name}</span>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export const AdmissionsBand = ({ school, brand, applyUrl }) => (
  <Section id="admissions" testId="school-admissions-section" className="bg-slate-950 py-24 relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.18] pointer-events-none"
         style={{ background: `radial-gradient(60rem 30rem at 15% 0%, ${brand}, transparent 60%)` }} />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <SectionHead light kicker="Admissions" title="Join us this intake" brand={brand}
                       blurb={school.admissions_note} />
          <ol className="mt-10 space-y-5">
            {(school.apply_steps || []).map((s, i) => (
              <li key={s} className="flex gap-4" data-testid={`apply-step-${i}`}>
                <span className="shrink-0 h-7 w-7 rounded-full grid place-items-center text-[11px] font-bold text-white"
                      style={{ backgroundColor: brand }}>{i + 1}</span>
                <span className="text-white/75 text-sm leading-relaxed pt-1">{s}</span>
              </li>
            ))}
          </ol>
          <Link to={applyUrl}>
            <Button size="lg" data-testid="apply-now-admissions-btn"
                    className="mt-10 rounded-full px-8 font-semibold text-white shadow-lg hover:brightness-110 active:scale-95 transition-all"
                    style={{ backgroundColor: brand }}>
              {school.hero_cta_label || 'Apply Now'} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="lg:col-span-6">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase text-white/50">Open intakes</p>
          <div className="mt-5 space-y-3">
            {(school.intakes || []).length === 0 ? (
              <p className="rounded-2xl border border-white/10 bg-white/5 px-6 py-8 text-sm text-white/60"
                 data-testid="intakes-empty">
                No intake is open right now. Applications are still welcome — the admissions office
                will place you in the next one.
              </p>
            ) : school.intakes.map((i, idx) => (
              <div key={`${i.name}-${idx}`} data-testid={`intake-card-${idx}`}
                   className="rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur px-6 py-5
                              transition-colors hover:bg-white/[0.1]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white font-semibold">{i.name}</p>
                    <p className="text-xs text-white/50 mt-0.5">
                      {[i.academic_year_name, i.term_name].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <Badge className="border-0 text-[10px] shrink-0"
                         style={{ backgroundColor: `${brand}33`, color: '#fff' }}>Open</Badge>
                </div>
                {(i.opens_on || i.closes_on) && (
                  <p className="mt-4 flex items-center gap-2 text-xs text-white/60 font-mono">
                    <Clock className="h-3.5 w-3.5" />
                    {i.opens_on || '—'} → {i.closes_on || 'until filled'}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Section>
);

export const NewsBand = ({ announcements, brand }) => {
  const items = (announcements || []).slice(0, 3);
  if (!items.length) return null;
  return (
    <Section id="news" testId="school-news-section" className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <SectionHead kicker="Latest" title="News & notices" brand={brand} />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((a, idx) => (
          <article key={`${a.title}-${idx}`} data-testid={`news-card-${idx}`}
                   style={{ animationDelay: `${idx * 90}ms` }}
                   className="fw-stagger rounded-2xl border border-slate-200 bg-white p-7 flex flex-col
                              transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.3)]">
            <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
              {a.date && new Date(a.date).toLocaleDateString(undefined,
                { day: 'numeric', month: 'short', year: 'numeric' })}
              {a.priority === 'urgent' && (
                <Badge variant="destructive" className="text-[10px]">Urgent</Badge>
              )}
            </div>
            <h3 className="fw-display mt-4 text-lg font-semibold text-slate-900 leading-snug">{a.title}</h3>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-4">{a.body}</p>
          </article>
        ))}
      </div>
    </Section>
  );
};

export const TestimonialsBand = ({ testimonials, brand }) => {
  if (!testimonials.length) return null;
  return (
    <Section id="testimonials" testId="school-testimonials-section" className="bg-slate-50/70 py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <SectionHead kicker="In their words" title="What our community says" brand={brand} />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <blockquote key={`${t.name}-${idx}`} data-testid={`testimonial-${idx}`}
                        style={{ animationDelay: `${idx * 80}ms` }}
                        className="fw-stagger rounded-2xl bg-white border border-slate-200 p-8 flex flex-col">
              <Quote className="h-6 w-6 mb-5 opacity-30" style={{ color: brand }} />
              <p className="text-slate-700 leading-relaxed italic">“{t.quote}”</p>
              <footer className="mt-7 pt-6 border-t border-slate-100 flex items-center gap-3">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={t.name}
                       className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200" />
                ) : (
                  <span className="h-10 w-10 rounded-full grid place-items-center text-xs font-bold text-white"
                        style={{ backgroundColor: brand }}>
                    {(t.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                  </span>
                )}
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{t.name}</span>
                  {t.role && <span className="block text-xs text-slate-500">{t.role}</span>}
                </span>
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </Section>
  );
};

export const GalleryBand = ({ urls, brand, apiUrl }) => {
  const [open, setOpen] = useState(null);
  if (!urls.length) return null;
  const src = (u) => (u.startsWith('http') ? u : `${apiUrl}${u}`);
  return (
    <Section id="gallery" testId="school-gallery-section" className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <SectionHead kicker="Campus life" title="Gallery" brand={brand} />
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {urls.map((u, idx) => (
          <button key={`${u.slice(-18)}-${idx}`} type="button" onClick={() => setOpen(src(u))}
                  data-testid={`gallery-item-${idx}`}
                  className={`overflow-hidden rounded-2xl bg-slate-100 ${idx % 7 === 0 ? 'sm:row-span-2 aspect-[3/4]' : 'aspect-[4/3]'}`}>
            <img src={src(u)} alt={`Campus ${idx + 1}`} loading="lazy"
                 className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
          </button>
        ))}
      </div>
      {open && (
        <div role="presentation" onClick={() => setOpen(null)} data-testid="gallery-lightbox"
             className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm grid place-items-center p-6">
          <img src={open} alt="Campus" className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white"
                  onClick={() => setOpen(null)} data-testid="gallery-lightbox-close">
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
    </Section>
  );
};

export const ContactBand = ({ school, brand }) => {
  const socials = [
    ['Facebook', school.social_facebook], ['X', school.social_twitter],
    ['Instagram', school.social_instagram], ['YouTube', school.social_youtube],
    ['LinkedIn', school.social_linkedin],
  ].filter(([, v]) => v);
  const rows = [
    school.contact_email && { icon: Mail, label: school.contact_email, href: `mailto:${school.contact_email}` },
    school.contact_phone && { icon: Phone, label: school.contact_phone, href: `tel:${school.contact_phone}` },
    school.address && { icon: MapPin, label: school.address },
    school.website_url && { icon: Globe, label: school.website_url, href: school.website_url },
  ].filter(Boolean);
  return (
    <Section id="contact" testId="school-contact-section" className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHead kicker="Get in touch" title="Visit or call us" brand={brand} />
          <div className="mt-9 space-y-5">
            {rows.map((r) => (
              <div key={r.label} className="flex items-start gap-4">
                <r.icon className="h-4 w-4 mt-1 shrink-0" style={{ color: brand }} />
                {r.href ? (
                  <a href={r.href} target={r.href.startsWith('http') ? '_blank' : undefined}
                     rel="noreferrer" className="text-slate-700 hover:text-slate-900 break-all">
                    {r.label}
                  </a>
                ) : <span className="text-slate-700">{r.label}</span>}
              </div>
            ))}
            {rows.length === 0 && (
              <p className="text-sm text-slate-500">Contact details are being updated.</p>
            )}
          </div>
          {socials.length > 0 && (
            <div className="mt-9 flex flex-wrap gap-2" data-testid="school-socials">
              {socials.map(([label, url]) => (
                <a key={label} href={url} target="_blank" rel="noreferrer"
                   className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600
                              hover:border-slate-900 hover:text-slate-900 transition-colors">
                  {label}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          {school.address ? (
            <iframe title="Campus location" data-testid="school-map"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(school.address)}&output=embed`}
                    className="w-full h-[380px] rounded-3xl border border-slate-200" loading="lazy" />
          ) : (
            <div className="h-[380px] rounded-3xl border border-dashed border-slate-300 grid place-items-center
                            text-sm text-slate-400 gap-2">
              <Sparkles className="h-5 w-5" /> Add an address to show the campus map
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export const CustomBands = ({ sections, brand }) => (
  <>
    {(sections || []).filter((s) => s.title || s.body).map((s, idx) => (
      <Section key={`${s.title}-${idx}`} testId={`school-custom-section-${idx}`}
               className="mx-auto max-w-7xl px-6 lg:px-8 pb-24">
        <SectionHead title={s.title} brand={brand} />
        <p className="mt-6 max-w-3xl text-base leading-[1.9] text-slate-600 whitespace-pre-line">{s.body}</p>
      </Section>
    ))}
  </>
);
